import React, { useEffect, useState } from 'react'
import { Typography, Input, Button, Spinner, Alert } from '@material-tailwind/react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { PiEnvelope } from 'react-icons/pi'
import { colors } from '@material-tailwind/react/types/generic'
import { logger } from '../utils/helpers/logger'
import { signInWithEmail } from '../utils/helpers/supabase.helpers'
import { useSessionContext } from '../contexts/SessionContext'
import { useGeneralToast } from '../hooks/toast'
import { usePageTitle } from '../hooks/toolkit'
import Container from '../components/Container'
import { useDarkMode } from '../contexts/DarkModeContext'

const CreateAccount = () => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  usePageTitle(t('createAccount.pageTitle'))

  const { success: toastSuccess, error: toastError } = useGeneralToast()
  const { user } = useSessionContext()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [canResend, setCanResend] = useState(true)
  const [cooldown, setCooldown] = useState(0)

  useEffect(() => {
    if (cooldown <= 0) {
      return
    }

    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          setCanResend(true)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [cooldown])

  if (user) {
    // send a logged user to its dashboard
    return <Navigate to="/dashboard" state={{ from: location }} replace />
  }

  const register = async (e: React.FormEvent) => {
    setLoading(true)

    e.stopPropagation()
    e.preventDefault()

    if (!email) {
      setLoading(false)
      return
    }

    const { error } = await signInWithEmail({ email })

    if (error) {
      toastError(t('createAccount.somethingWentWrong'))
      setLoading(false)
      setSuccess(false)
      return
    }

    toastSuccess(t('createAccount.accountCreatedSuccessfully'))
    setLoading(false)
    setSuccess(true)
    setCanResend(false)
    setCooldown(60)
  }

  const resendEmail = async () => {
    if (!email || resendLoading || !canResend) {
      return
    }

    setResendLoading(true)
    const { error } = await signInWithEmail({ email })

    if (error) {
      logger.error(error, 'ResendEmail')
      toastError(t('createAccount.resendEmailError'))
      setResendLoading(false)
      return
    }

    toastSuccess(t('createAccount.resendEmailSuccess'))
    setResendLoading(false)
    setCanResend(false)
    setCooldown(60)
  }

  return (
    <Container direction="col">
      <Typography className="mt-4 mb-12" variant="h1" data-testid="register-title">
        {t('createAccount.title')}
      </Typography>

      {!success && (
        <form className="w-full md:w-1/2 flex flex-col gap-8" onSubmit={register}>
          <Input
            {...(darkMode && { color: 'white' })}
            value={email || ''}
            onChange={(e) => setEmail(e.currentTarget.value)}
            type="email"
            label={t('createAccount.email')}
            required
            disabled={success}
            data-testid="register-email-input"
          />
          <Button type="submit" disabled={loading} data-testid="register-submit-btn">
            {loading ? <Spinner className="h-4 w-4 mx-auto" /> : t('createAccount.register')}
          </Button>
        </form>
      )}

      {success && (
        <div className="w-full md:w-1/2 flex flex-col mt-8">
          <Alert
            color="blue"
            icon={<PiEnvelope className="w-6 h-6" />}
            variant={darkMode ? 'filled' : 'ghost'}
            data-testid="register-success-alert"
          >
            <Typography variant="h3">{t('createAccount.successMessageTitle')}</Typography>
            <Typography>
              <Trans
                i18nKey="createAccount.successMessage"
                values={{ email }}
                components={{
                  strong: <strong className="font-black" />,
                }}
              />
            </Typography>
          </Alert>
          <div className="flex items-center justify-center gap-2 mt-4">
            <Typography>{t('createAccount.resendPrompt')}</Typography>
            <Button
              variant="outlined"
              color={(darkMode ? 'white' : 'primary') as colors}
              size="sm"
              onClick={resendEmail}
              disabled={resendLoading || !canResend}
              data-testid="register-resend-btn"
            >
              {resendLoading ? <Spinner className="h-4 w-4" /> : t('createAccount.resendEmail')}
            </Button>
          </div>
          {!canResend && (
            <Typography className="mt-2 text-center" variant="small">
              {t('createAccount.resendCooldownMessage', { seconds: cooldown })}
            </Typography>
          )}
        </div>
      )}

      <hr className="w-full md:w-1/2 my-6 opacity-20 border-primary dark:border-white" />

      <div className="flex w-full justify-center items-center gap-2 my-2">
        <Typography>{t('createAccount.dontHaveBch')}</Typography>
        <Link
          to="https://www.indiegogo.com/projects/agami-everywhere-immersive-sound"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Typography className="underline font-bold">{t('createAccount.buyNow')}</Typography>
        </Link>
      </div>

      <div className="flex w-full justify-center items-center gap-2 my-2">
        <Typography>{t('createAccount.alreadyHaveAccount')}</Typography>
        <Link to="/signin">
          <Typography className="underline font-bold">{t('createAccount.signIn')}</Typography>
        </Link>
      </div>
    </Container>
  )
}

export default CreateAccount
